import React from 'react';
import { useTheme } from '../../hooks/useTheme';

const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  disabled = false, 
  onClick, 
  type = 'button',
  className = '',
  isDarkMode = false, // legacy; el color sale del ThemeContext
  ...props 
}) => {
  const theme = useTheme();
  void isDarkMode;
  const baseStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontWeight: theme.typography.fontWeight.medium,
    border: 'none',
    borderRadius: theme.borderRadius.md,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease-in-out',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    outline: 'none',
    boxSizing: 'border-box',
    ...props.style
  };

  const sizeStyles = {
    sm: {
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      fontSize: theme.typography.fontSize.sm,
      minHeight: '32px'
    },
    md: {
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      fontSize: theme.typography.fontSize.base,
      minHeight: '36px'
    },
    lg: {
      padding: `${theme.spacing.lg} ${theme.spacing.xl}`,
      fontSize: theme.typography.fontSize.lg,
      minHeight: '44px'
    }
  };

  const variantStyles = {
    primary: {
      backgroundColor: disabled ? theme.colors.interactive.secondary : theme.colors.interactive.primary,
      color: theme.colors.text.accent,
      border: `1px solid ${disabled ? theme.colors.interactive.secondary : theme.colors.interactive.primary}`,
      '&:hover': {
        backgroundColor: disabled ? theme.colors.interactive.secondary : theme.colors.interactive.primaryHover,
        borderColor: disabled ? theme.colors.interactive.secondary : theme.colors.interactive.primaryHover,
      }
    },
    secondary: {
      backgroundColor: disabled ? theme.colors.tertiary : theme.colors.secondary,
      color: disabled ? theme.colors.text.muted : theme.colors.text.primary,
      border: `1px solid ${theme.colors.border.primary}`,
      '&:hover': {
        backgroundColor: disabled ? theme.colors.tertiary : theme.colors.hover,
        borderColor: disabled ? theme.colors.border.primary : theme.colors.border.secondary,
      }
    },
    outline: {
      backgroundColor: 'transparent',
      color: disabled ? theme.colors.text.muted : theme.colors.interactive.primary,
      border: `1px solid ${disabled ? theme.colors.border.primary : theme.colors.interactive.primary}`,
      '&:hover': {
        backgroundColor: disabled ? 'transparent' : theme.colors.interactive.primary,
        color: disabled ? theme.colors.text.muted : theme.colors.text.accent,
      }
    },
    ghost: {
      backgroundColor: 'transparent',
      color: disabled ? theme.colors.text.muted : theme.colors.text.primary,
      border: '1px solid transparent',
      '&:hover': {
        backgroundColor: disabled ? 'transparent' : theme.colors.hover,
      }
    },
    danger: {
      backgroundColor: disabled ? theme.colors.interactive.secondary : theme.colors.interactive.danger,
      color: theme.colors.text.accent,
      border: `1px solid ${disabled ? theme.colors.interactive.secondary : theme.colors.interactive.danger}`,
      '&:hover': {
        backgroundColor: disabled ? theme.colors.interactive.secondary : theme.colors.interactive.dangerHover,
        borderColor: disabled ? theme.colors.interactive.secondary : theme.colors.interactive.dangerHover,
      }
    },
    success: {
      backgroundColor: disabled ? theme.colors.interactive.secondary : theme.colors.interactive.success,
      color: theme.colors.text.accent,
      border: `1px solid ${disabled ? theme.colors.interactive.secondary : theme.colors.interactive.success}`,
      '&:hover': {
        backgroundColor: disabled ? theme.colors.interactive.secondary : theme.colors.interactive.successHover,
        borderColor: disabled ? theme.colors.interactive.secondary : theme.colors.interactive.successHover,
      }
    }
  };

  const handleMouseEnter = (e) => {
    // Hover effect disabled - no color changes on mouse over
    return;
  };

  const handleMouseLeave = (e) => {
    // Hover effect disabled - no color changes on mouse leave
    return;
  };

  const combinedStyles = {
    ...baseStyles,
    ...sizeStyles[size],
    ...variantStyles[variant]
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={className}
      style={combinedStyles}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button;
